/**
 * Hook for checking user permissions in frontend components.
 */
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';

export interface UsePermissionsReturn {
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has all of the specified permissions */
  hasAllPermissions: (permissions: string[]) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Check if user is a host user */
  isHost: boolean;
  /** Check if user has admin role */
  isAdmin: boolean;
  /** Check if user has manager role */
  isManager: boolean;
  /** Check if user can manage users */
  canManageUsers: boolean;
  /** Check if user can view users */
  canViewUsers: boolean;
  /** Get user's current role */
  role: UserRole | undefined;
  /** Get all user permissions */
  permissions: string[];
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  const permissions = user?.permissions || [];
  const isHost = user?.is_host || false;

  const hasPermission = (permission: string): boolean => {
    // Host users have all permissions
    if (isHost) return true;
    // Check wildcard permission
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(perm => hasPermission(perm));
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(perm => hasPermission(perm));
  };

  const role = user?.role;
  const isAdmin = role === 'admin';
  const isManager = role === 'manager';

  const canManageUsers = hasAnyPermission(['users:invite', 'users:edit', 'users:delete', 'users:change_role']);
  const canViewUsers = hasPermission('users:view');

  return {
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isHost,
    isAdmin,
    isManager,
    canManageUsers,
    canViewUsers,
    role,
    permissions,
  };
}

/**
 * Component wrapper that only renders children if user has required permission(s).
 */
interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
}

export function PermissionGate({
  children,
  permission,
  permissions,
  requireAll = true,
  fallback = null,
}: PermissionGateProps): React.ReactNode {
  const { hasPermission, hasAllPermissions, hasAnyPermission } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  } else {
    hasAccess = true; // No permission required
  }

  return hasAccess ? children : fallback;
}

export default usePermissions;
